import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import _ from '@lodash';
import * as yup from 'yup';
import { Accordion, AccordionSummary, AccordionDetails, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import ClientModel from '../../main/apps/clients/model/ClientModel';
import { selectClientById, updateClient, createClient } from 'app/store/clientsSlice';
import { phoneRegex } from 'app/shared-components/utils';
import specializations from 'app/shared-components/controls/specializations.json';
import states from 'app/shared-components/controls/states.json';
import genders from 'app/shared-components/controls/genders.json';
import clientTypes from 'app/shared-components/controls/clientTypesSingular.json';
import languages from 'app/shared-components/controls/languages.json';
import clientRaces from 'app/shared-components/controls/clientRaces.json';
import ethnicities from 'app/shared-components/controls/ethnicities.json';
import maritalStatuses from 'app/shared-components/controls/maritalStatuses.json';
import clientCategories from 'app/shared-components/controls/clientCategories.json';
import ControlledTextField from 'app/shared-components/form/ControlledTextField';
import ControlledMultiSelect from 'app/shared-components/form/ControlledMultiSelect';
import ControlledSingleSelect from 'app/shared-components/form/ControlledSingleSelect';
import ControlledWYSIWYG from 'app/shared-components/form/ControlledWYSIWYG';
import ControlledCheckbox from 'app/shared-components/form/ControlledCheckbox';
import dispatchSubmit from 'app/shared-components/util/dispatchSubmit';
import EntityForm from 'app/shared-components/layout/EntityForm';
import {getClients} from "app/store/groupsSlice";
import {useSnackbar} from "notistack";

const defaultValues= ClientModel();

/**
 * Form Validation Schema
 */
const createSchema = (isNew) => yup.object().shape({
  firstName: yup.string().required('You must enter first name'),
  middleName: yup.string(),
  lastName: yup.string().required('You must enter last name'),
  dateOfBirth: yup.string(),
  governmentIdNumber: yup.string(),
  email: yup.string().email('You must enter a valid email'),
  phone: yup.string().matches(phoneRegex, {message: 'Phone number is not valid', excludeEmptyString: true }),
  preferredLanguage: yup.string(),
  race: yup.string(),
  ethnicity: yup.string(),
  maritalStatus: yup.string(),
  deceased: yup.boolean(),
  dateOfDeath: yup.string(),
  city: yup.string(),
  stateProvince: yup.string(),
  postalCode: yup.string(),
  gender: yup.string(),
  ageDemographic: yup.string(),
  clientCategory: yup.string(),
  about: yup.string(),
  treatmentAreas: yup.array()
});


function ClientForm() {
  const dispatch = useDispatch();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const routeParams = useParams();
  const clientId = routeParams.clientId ? routeParams.clientId : routeParams.id;
  const organizationId = routeParams.groupId;
  const isOrganization = Boolean(organizationId);
  const isNew = clientId === 'new';
  const selectedClient= useSelector(selectClientById(isNew ? undefined : clientId)); //Always need to run selector for react hook order
  const client = isNew ? ClientModel() : ClientModel(selectedClient);
  const title= isNew ? "New Client" : "Edit " + client?.firstName + " " + client?.lastName;
  const returnPath=  isOrganization ? `/apps/organization/` + organizationId + `/clients` : `/apps/clients`;

  const { control, watch, reset, handleSubmit, formState, getValues } = useForm({
	defaultValues,
    mode: 'onChange',
    resolver: yupResolver(createSchema(isNew)),
  });
  
  const watchDeceased = watch('deceased');
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    demographics: false,
    contact: false,
    clinical: false
  });
  
  const handleSectionChange = (section) => (event, isExpanded) => {
    setExpandedSections(prev => ({ ...prev, [section]: isExpanded }));
  };

  const { isValid, dirtyFields, errors } = formState;

  useEffect(() => {
    reset({ ...client });
  }, [ clientId, reset]);

  /**
   * Form Submit
   */
  function onSubmit(data) {
	const client= ClientModel({ ...data, id: clientId, channelId: organizationId});
	dispatchSubmit({dispatch: dispatch, 
	                submitFunction: updateClient(client), 
	                returnPath: returnPath, 
	                successMessage:  "Client Updated!"});
  }

  function onSubmitNew(data) {
	const client= ClientModel({ ...data, channelId: organizationId});
	dispatchSubmit({dispatch: dispatch, 
	                submitFunction: createClient(client), 
	                returnPath: returnPath, 
	                successMessage:  "Client Created!"});
      if (isOrganization) {
          // Add a 5-second delay before fetching clients to refresh with new Client team
          setTimeout(() => {
              dispatch(getClients({
                  groupId: organizationId,
                  type: "organization",
                  closeSnackbar,
                  enqueueSnackbar
              }));
          }, 5000);
      }
  }

  return (
    <EntityForm title={title} 
	            submitTitle={isNew ? "Create" : "Update"} 
	            onSubmit={isNew ? onSubmitNew : onSubmit}
	            handleSubmit={handleSubmit} isValid={isValid} dirtyFields={dirtyFields}
	            returnPath={returnPath}
	>  
        
        {/* Basic Information */}
        <Accordion expanded={expandedSections.basic} onChange={handleSectionChange('basic')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Basic Information</Typography>
          </AccordionSummary>
          <AccordionDetails className="flex flex-col gap-16">
            <ControlledTextField name="firstName" label="First name"
                                 control={control} errors={errors}
                 				 autoFocus required fullWidth />
            
            <ControlledTextField name="middleName" label="Middle name"
                                 control={control} errors={errors}
                 				 fullWidth />
            
            <ControlledTextField name="lastName" label="Last name"
                                 control={control} errors={errors}
                 				 required fullWidth />
            
            <ControlledSingleSelect name="ageDemographic" label="Age Demographic"
                                    control={control} errors={errors}
                                    items={clientTypes} />
            
            <ControlledSingleSelect name="clientCategory" label="Client Category"
                                    control={control} errors={errors}
                                    items={clientCategories} />
            
            <ControlledTextField name="clientUserId" label="Linked User ID"
                                 control={control} errors={errors}
                                 helperText="User account linked to this client record"
                                 fullWidth />
          </AccordionDetails>
        </Accordion>
        
        {/* Demographics */}
        <Accordion expanded={expandedSections.demographics} onChange={handleSectionChange('demographics')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Demographics</Typography>
          </AccordionSummary>
          <AccordionDetails className="flex flex-col gap-16">
            <ControlledTextField name="dateOfBirth" label="Date of Birth" type="date"
                                 control={control} errors={errors}
                                 InputLabelProps={{ shrink: true }}
                 				 fullWidth />
            
            <ControlledTextField name="governmentIdNumber" label="Government ID Number"
                                 control={control} errors={errors}
                                 type="password"
                 				 fullWidth />
            
            <ControlledSingleSelect name="gender" label="Gender"
                                    control={control} errors={errors}
                                    items={genders} />
            
            <ControlledSingleSelect name="preferredLanguage" label="Preferred Language"
                                    control={control} errors={errors}
                                    items={languages} />
            
            <ControlledSingleSelect name="race" label="Race"
                                    control={control} errors={errors}
                                    items={clientRaces} />
            
            <ControlledSingleSelect name="ethnicity" label="Ethnicity"
                                    control={control} errors={errors}
                                    items={ethnicities} />
            
            <ControlledSingleSelect name="maritalStatus" label="Marital Status"
                                    control={control} errors={errors}
                                    items={maritalStatuses} />
            
            <ControlledCheckbox name="deceased" label="Deceased"
                               control={control} errors={errors} />
            
            {watchDeceased && (
              <ControlledTextField name="dateOfDeath" label="Date of Death" type="date"
                                   control={control} errors={errors}
                                   InputLabelProps={{ shrink: true }}
                   				   fullWidth />
            )}
          </AccordionDetails>
        </Accordion>
        
        {/* Contact Information */}
        <Accordion expanded={expandedSections.contact} onChange={handleSectionChange('contact')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Contact Information</Typography>
          </AccordionSummary>
          <AccordionDetails className="flex flex-col gap-16">
            <ControlledTextField name="email" label="Email" type="email"
                                 control={control} errors={errors}
                 				 fullWidth />
                 				 
        	<ControlledTextField name="phone" label="Phone"
                                 control={control} errors={errors}
                 				 fullWidth />
                                    
            <ControlledTextField name="city" label="City"
                                 control={control} errors={errors}
                 				 fullWidth />
            
            <ControlledSingleSelect name="stateProvince" label="State"
                                    control={control} errors={errors}
                                    items={states} />
     
     		<ControlledTextField name="postalCode" label="Postal Code"
                                 control={control} errors={errors}
                 				 fullWidth />
          </AccordionDetails>
        </Accordion>
        
        {/* Clinical Information */}
        <Accordion expanded={expandedSections.clinical} onChange={handleSectionChange('clinical')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Clinical Information</Typography>
          </AccordionSummary>
          <AccordionDetails className="flex flex-col gap-16">
     		<ControlledMultiSelect name="treatmentAreas" label="Treatment Areas"
                                    control={control} errors={errors}
                                    items={specializations} maxHeight="300" />

            
            <ControlledWYSIWYG name="about"
              				   control={control}
              				   key={clientId}
              				   initialContent={client.about} />
          </AccordionDetails>
        </Accordion>

    </EntityForm>
  );
}

export default ClientForm;
